/* eslint-disable no-unused-vars */

import React, { useEffect, useState } from "react"
import qs from "query-string"
import _ from "lodash"
import styled from "styled-components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faComments,
  faLink,
  faTimes,
} from "@fortawesome/free-solid-svg-icons"
import { useUpdateInboxHeader } from "../../hooks/useUpdateInboxHeader"
import theme from "../../stylesheets/theme"

const INBOX_URL = process.env.REACT_APP_INBOX_URL
const SERVER_API_PATH_GROUP = process.env.REACT_APP_SERVER_GROUP_API_PATH

const MessageBoxContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f5f5f5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`

const Header = styled.div`
  background: #0000FF;
  color: white;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`

const Content = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
`

const Section = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`

const SectionTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  
`

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px;
  border-top: 1px solid ${theme.gray1};
  background: ${theme.gray0};
  margin-top: 4px;
`

const StyledActionButton = styled.button`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  ${(props) => props.primary && `
    background: #0000FF;
    color: ${theme.white};
  
  `}
  
  ${(props) => props.secondary && `
    background: #0000FF;
    color: ${theme.white};
    border: 1px solid ${theme.gray2};
  `}
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
  
  &:disabled {
    background: ${theme.gray3};
    color: ${theme.gray5};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`
const InviteSection = styled.div`
  border-radius: 8px;
`
const InviteLink = styled.div`
  border-radius: 8px;
  line-height: 1.5;
  overflow: hidden;
  width: 100%;
`
const InviteLinkText = styled.a`
  font-size: 14px;
  color: ${theme.primary};
  text-decoration: none;
  cursor: pointer;
  margin-right: 8px;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  
  &:hover {
    text-decoration: underline;
    color: ${theme.primary};
  }
`

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #fcc;
  margin-bottom: 12px;
  font-size: 14px;
`

const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  max-height: 600px;
  height: 250px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 6px;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #0000FF;
    box-shadow: 0 0 0 2px rgba(0, 0, 255, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`

const MessageBox = ({ location }) => {
  const [signedContext, setSignedContext] = useState(null)
  const [payload, setPayload] = useState(null)
  const [contextToken, setContextToken] = useState(null)
  const [groupInviteLink, setGroupInviteLink] = useState("")
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState("")
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)

  // Parse URL parameters
  const query = qs.parse(location?.search || window.location.search)

  const selectedContactsFromQuery = query?.selectedContacts ? JSON.parse(query.selectedContacts) : []

  useEffect(() => {
    // Extract group invite link from query parameters
    if (query.groupInviteLink) {
      setGroupInviteLink(query.groupInviteLink)
    }
    setLoading(false)
  }, [query.groupInviteLink])

  useUpdateInboxHeader({ title: "Send an invitation to join the group", isGroupsView: true })

  const receiveMessage = (event) => {
    const whitelistedOrigins = INBOX_URL.split("||")
    if (!whitelistedOrigins.includes(event.origin)) {
      return
    }
    if (event?.data) {
      console.log("Event Data", event.data)

      if (!_.isEmpty(event.data?.payload)) {
        setPayload(event.data?.payload)
        setSignedContext(event.data?.signedContext)
        setContextToken(event.data?.ctx)
      }
    }
  }

  useEffect(() => {
    if (navigator.userAgent !== "ReactSnap") {
      window.addEventListener("message", receiveMessage, false)
      window.parent.postMessage("iframeFinishLoading", "*")
    }
    return () => {
      window.removeEventListener("message", receiveMessage, false)
    }
  }, [])

  const handleSendInviteViaMessage = async () => {
    if (!message.trim()) {
      setError("Please enter a message before sending.")
      return
    }

    // Check contacts before starting the sending process
    if (selectedContactsFromQuery.length === 0) {
      setError("Please select at least one contact to send the invitation.")
      return
    }

    setSending(true)
    setError(null)

    try {
      // Create the message text, optionally including the group invite link
      let messageText = message
      if (groupInviteLink) {
        messageText = `${message}`
      }

      const members = selectedContactsFromQuery
        ?.filter((contact) => !contact.isManuallyAdded)
        ?.map((contact) => contact.id || contact.memberId)
        ?.filter(Boolean)

      const recipientIds = selectedContactsFromQuery
        ?.filter((contact) => contact.isManuallyAdded)
        ?.map((contact) => contact.phone || contact.recipientId)
        ?.filter(Boolean)

      console.log("Processed contacts:", {
        totalContacts: selectedContactsFromQuery?.length,
        members: members.length,
        recipientIds: recipientIds.length,
      })

      const response = [
        {
          text: messageText,
          type: "TEXT",
        },
      ]

      const body = {
        response,
        ...(members.length > 0 && { members }),
        ...(recipientIds.length > 0 && { recipientIds }),
      }

      const result = await fetch(`${SERVER_API_PATH_GROUP}/group/send-invite-link`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Woztell-Payload": JSON.stringify(payload),
          "X-Woztell-SignedContext": signedContext,
          "X-Woztell-Context": contextToken,
        },
        body: JSON.stringify(body),
      }).then((res) => res.json())

      setSending(false)
      if (result.ok) {
        console.log("Message sent successfully:", result)
        // Notify parent window of success
        window.parent.postMessage({
          type: "MESSAGE_SENT",
          payload: {
            success: true,
            message: "Message sent successfully",
          },
        }, "*")
        window.parent.postMessage("iframeCloseModal", "*")
      } else {
        setError(result?.error?.message ?? "Failed to send message. Please try again.")
      }
    } catch (err) {
      setSending(false)
      setError("Failed to send message. Please try again.")
      console.error("Error sending message:", err)
    }
  }

  console.log("Current state:", {
    loading,
    groupInviteLink,
    contextToken,
    payload: !!payload,
    signedContext: !!signedContext,
    selectedContactsFromQuery,
  })

  return (
    <MessageBoxContainer>

      <Content>
        <SectionTitle>Type your message</SectionTitle>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <StyledTextArea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Enter your message here..."
        />
        {groupInviteLink && (
          <InviteSection>
            <InviteLink>
              <InviteLinkText
                href={groupInviteLink || "https://chat.whatsapp.com/some-thing-went-wrong"}
                target="_blank"
                rel="noopener noreferrer"
              >
                {groupInviteLink || "https://chat.whatsapp.com/some-thing-went-wrong"}
              </InviteLinkText>
            </InviteLink>
          </InviteSection>
        )}

        <ActionButtonsContainer>
          <StyledActionButton
            secondary
            onClick={() => handleSendInviteViaMessage()}
            disabled={sending || !message.trim()}
          >
            {sending ? "Sending..." : "Send"}
          </StyledActionButton>
        </ActionButtonsContainer>
      </Content>
    </MessageBoxContainer>
  )
}

export default MessageBox
